import React, { useState, useEffect } from "react";
import { FaMapMarkerAlt, FaEnvelope } from "react-icons/fa";
import TotalPointsTag from "../../../../components/TotalPointsTag/TotalPointsTag";
import BuilderCard from "../../../../components/builderCard/builderCard";

const themes = {
  default: "bg-gray-200",
  nature: "bg-gradient-to-r from-green-300 to-green-600",
  neon: "bg-gradient-to-r from-pink-500 to-orange-500",
  space: "bg-gradient-to-r from-gray-800 via-gray-700 to-gray-900",
  ocean: "bg-gradient-to-r from-cyan-400 to-blue-500",
  minimal: "bg-gradient-to-r from-blue-50 to-blue-100",
};

const formatDate = (timestamp) => {
  if (!timestamp) return "N/A";
  let dateObj;
  if (timestamp._seconds) dateObj = new Date(timestamp._seconds * 1000);
  else if (timestamp.seconds) dateObj = new Date(timestamp.seconds * 1000);
  else if (typeof timestamp === "string" && !isNaN(Date.parse(timestamp))) dateObj = new Date(timestamp);
  else if (timestamp instanceof Date && !isNaN(timestamp.getTime())) dateObj = timestamp;
  else return "N/A";
  return dateObj.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

const ProfileSection = ({ user, isAdmin, onOpenDM, setUser, preferences ={} }) => {
  const [editMode, setEditMode] = useState(false);
  const [updatedUser, setUpdatedUser] = useState({ ...user });
  const [profileImage, setProfileImage] = useState(user.profileImage?.trim() ? user.profileImage : "/person.png");
  const [selectedImage, setSelectedImage] = useState(null);
  const [coverTheme, setCoverTheme] = useState(user.coverTheme || "default");
  const [hideEmail, setHideEmail] = useState(user.emailPrivate || false);
  const [userStats, setUserStats] = useState({ projectsOwned: 0, rolesTaken: 0, badges: [], categoriesCreated: [] });

  const hideRolePrefs = !isAdmin && preferences.hideRolePrefs;
  const hideContributions = !isAdmin && preferences.hideContributions;


  useEffect(() => {
    setUpdatedUser({ ...user });
    setHideEmail(user.emailPrivate || false);
    setCoverTheme(user.coverTheme || "default");
    setProfileImage(user.profileImage?.trim() ? user.profileImage : "/person.png");
  }, [user]);

  useEffect(() => {
    const fetchUserStats = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/user/${encodeURIComponent(user.email)}/stats`);
        const data = await response.json();
        if (response.ok) setUserStats(data);
      } catch (err) {
        console.error("❌ Error fetching stats:", err);
      }
    };
    if (user?.email) fetchUserStats();
  }, [user]);

  const handleSelectedImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      setProfileImage(URL.createObjectURL(file));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUpdatedUser((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      let imageBase64 = null;
      if (selectedImage instanceof File) {
        const reader = new FileReader();
        reader.readAsDataURL(selectedImage);
        await new Promise((resolve) => (reader.onloadend = resolve));
        imageBase64 = reader.result.split(",")[1];
      }

      const requestBody = {
        ...updatedUser,
        allowMessages: updatedUser.allowMessages === true,
        coverTheme,
        profileImage: imageBase64,
        emailPrivate: hideEmail,
      };

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/user/${encodeURIComponent(user.email)}/update`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!res.ok) throw new Error("Failed to update profile");
      const data = await res.json();
      const finalUser = {
        ...data.updatedData,
        allowMessages: data.updatedData.allowMessages === true,
      };

      setUser(finalUser);
      setUpdatedUser(finalUser);
      setEditMode(false);
    } catch (err) {
      console.error("❌ Error saving profile:", err);
    }
  };

  const showDMIcon = user.allowMessages === true;

  return (
    <div className="w-full">
      <div className={`h-40 ${themes[coverTheme]} relative`}>
        {editMode && (
          <select
            className="absolute top-3 right-3 text-xs bg-opacity-90 border rounded px-2 py-1"
            value={coverTheme}
            onChange={(e) => setCoverTheme(e.target.value)}
          >
            {Object.keys(themes).map((theme) => (
              <option key={theme} value={theme}>{theme.charAt(0).toUpperCase() + theme.slice(1)}</option>
            ))}
          </select>
        )}
      </div>

      <div className="px-6 py-10 flex flex-col md:flex-row md:items-start md:space-x-8">
  {/* Profile Avatar */}
  <div className="relative -mt-20 w-32 h-32 mx-auto md:mx-0">
    <label htmlFor="upload" className="cursor-pointer group block relative">
      {profileImage && profileImage.trim().length > 5 ? (
        <img
          src={profileImage}
          alt="Profile"
          onError={(e) => {
            e.currentTarget.style.display = "none";
            const initials = `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase() || "?";
            e.currentTarget.insertAdjacentHTML(
              "afterend",
              `<div class="w-32 h-32 rounded-full bg-gradient-to-tr from-gray-100 to-gray-200 text-gray-700 flex items-center justify-center text-3xl font-bold ring-2 ring-white shadow-sm">${initials}</div>`
            );
          }}
          className="w-32 h-32 rounded-full border-4 border-white object-cover shadow-md transition group-hover:scale-105"
        />
      ) : (
        <div className="w-32 h-32 rounded-full bg-gradient-to-tr from-gray-100 to-gray-200 text-gray-700 flex items-center justify-center text-3xl font-bold ring-2 ring-white shadow-sm">
          {`${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase() || "?"}
        </div>
      )}
    </label>
    {editMode && (
      <input type="file" id="upload" accept="image/*" className="hidden" onChange={handleSelectedImage} />
    )}
  </div>

  {/* Profile Content */}
  <div className="flex-1 mt-6 md:mt-0">
    {/* Name + Actions */}
    <div className="flex items-center flex-wrap gap-2">
      {editMode ? (
        <>
          <input
            type="text"
            name="firstName"
            value={updatedUser.firstName}
            onChange={handleChange}
            className="border rounded px-2 py-1 text-sm"
          />
          <input
            type="text"
            name="lastName"
            value={updatedUser.lastName}
            onChange={handleChange}
            className="border rounded px-2 py-1 text-sm"
          />
        </>
      ) : (
        <h2 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
          {user.firstName} {user.lastName}
          <TotalPointsTag email={user.email} />
          {showDMIcon && (
            <FaEnvelope
              title="Send Direct Message"
              onClick={() => onOpenDM(user.email, user.firstName, user.lastName)}
              className="text-blue-500 cursor-pointer text-sm"
            />
          )}
        </h2>
      )}
    </div>

    {/* Details */}
    <div className="mt-3 text-sm text-gray-600 space-y-2">
      {editMode ? (
        <textarea
          name="interests"
          value={updatedUser.interests}
          onChange={handleChange}
          placeholder="Enter interests..."
          className="w-full border rounded px-2 py-1 text-sm"
        />
      ) : (
        <p>{user.interests || "Not specified"}</p>
      )}

      {editMode ? (
        <textarea
          name="bio"
          value={updatedUser.bio}
          onChange={handleChange}
          placeholder="Add a bio..."
          className="w-full border rounded px-2 py-1 text-sm"
        />
      ) : (
        user.bio && <p className="italic text-gray-700">{user.bio}</p>
      )}

      {!hideEmail && (
        <p className="text-xs text-gray-500">
          📧 {user.email}
        </p>
      )}
      <p className="text-xs text-gray-400">
        Joined {formatDate(user.createdAt)}
      </p>
    </div>

    {/* Admin Controls */}
    {isAdmin && (
      <div className="mt-4 flex gap-3">
        {editMode ? (
          <button
            className="bg-blue-600 text-white px-4 py-1.5 rounded-md shadow hover:bg-blue-700"
            onClick={handleSave}
          >
            Save
          </button>
        ) : (
          <button
            className="border border-blue-600 text-blue-600 px-4 py-1.5 rounded-md hover:bg-blue-50"
            onClick={() => setEditMode(true)}
          >
            Edit
          </button>
        )}

        {user.email === "fronteracloud@gmail.com" && (
          <button
            className="bg-black text-white px-4 py-1.5 rounded-md hover:bg-gray-800"
            onClick={() => window.location.assign(`/fronteracloud/profile/frontera-dashboard`)}
          >
            Open Admin Dashboard
          </button>
        )}
      </div>
    )}
  </div>
</div>


      <div className="mt-8">
  <div className="rounded-2xl p-[1px] bg-gradient-to-tr from-fuchsia-200/60 via-sky-200/60 to-emerald-200/60">
    <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">User Highlights</h3>

      {/* Grid of highlights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm hover:shadow-md transition">
          <div className="text-[11px] uppercase text-gray-500">Creations</div>
          <div className="text-lg font-semibold text-gray-900">{userStats.projectsOwned}</div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm hover:shadow-md transition">
          <div className="text-[11px] uppercase text-gray-500">Collaborations</div>
          <div className="text-lg font-semibold text-gray-900">
            {userStats.rolesTaken}
            {preferences.hideContributions && isAdmin && (
              <span className="ml-1 text-xs text-gray-400">🔒</span>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm hover:shadow-md transition">
  <div className="text-[11px] uppercase text-gray-500 mb-1">Roles</div>
  {userStats.badges.length > 0 ? (
    <div className="flex flex-wrap gap-2 mt-1">
      {userStats.badges.map((role, idx) => (
        <span
          key={idx}
          className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-indigo-50 to-indigo-100 text-indigo-700 text-xs font-medium shadow-sm"
        >
          {role}
        </span>
      ))}
    </div>
  ) : (
    <span className="text-sm text-gray-500">None</span>
  )}
  {preferences.hideRolePrefs && isAdmin && (
    <div className="mt-1 text-xs text-gray-400">🔒 Hidden from public</div>
  )}
</div>


        <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm hover:shadow-md transition">
          <div className="text-[11px] uppercase text-gray-500">Categories</div>
          <div className="flex flex-wrap gap-1 mt-1">
            {userStats.categoriesCreated.length > 0 ? (
              userStats.categoriesCreated.map((cat, idx) => (
                <span
                  key={idx}
                  className="bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full text-xs font-medium"
                >
                  {cat}
                </span>
              ))
            ) : (
              <span className="text-sm text-gray-500">None</span>
            )}
          </div>
        </div>
      </div>

      {/* Builder profile merged inside */}
      {user?.builderProfile && (
        <div className="mt-6">
          <h4 className="font-semibold mb-2 text-gray-900">Builder Profile</h4>
          <BuilderCard
            profile={user.builderProfile}
            identity={
              Array.isArray(user.builderProfile.identity)
                ? user.builderProfile.identity
                : [user.builderProfile.identity1, user.builderProfile.identity2].filter(Boolean)
            }
            profileImage={user.profileImage}
          />
        </div>
      )}
    </div>
  </div>
</div>

    </div>
  );
};

export default ProfileSection;